import { CategoryTag } from "@/components/CategoryTag";
import {
  BandcampIcon,
  MailIcon,
  PhoneIcon,
  SpotifyIcon,
  TiktokIcon,
} from "@/components/icons";
import theme from "@/styles/theme";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Grid, IconButton, Link, SvgIcon } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import { grey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/system";
import { StrapiImageAsset } from "../StrapiImageAsset";
import HelpIcon from "@mui/icons-material/Help";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useState } from "react";
import {
  basierCirleBold,
  basierCirleRegular,
  futuraNextCondensedExtraBold,
} from "@/styles/fonts";

export const LocationCard = ({ location, variant, page }) => {
  const [isReadMore, setIsReadMore] = useState(true);

  const ReadMore = ({ children }) => {
    const text = children;
    const [isReadMore, setIsReadMore] = useState(true);
    const toggleReadMore = () => {
      setIsReadMore(!isReadMore);
    };
    const readMoreStyled = {
      "& .readmore": {
        color: theme.palette.secondary.main,
        cursor: "pointer",
        "&:hover": {
          opacity: "0.75",
        },
      },
    };
    return (
      <Typography variant="body1" sx={readMoreStyled}>
        <>
          {location.description.length > 100 ? (
            <>
              {isReadMore
                ? location.description.slice(0, 100)
                : location.description}
              <span onClick={toggleReadMore} className="readmore">
                {isReadMore ? "... weiterlesen" : " weniger anzeigen"}
              </span>
            </>
          ) : (
            <>{location.description}</>
          )}
        </>
      </Typography>
    );
  };

  const style = {
    backgroundColor: grey[200],
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&.MuiPaper-root": {
      padding: "1rem",
    },
    "& .MuiCardMedia-root": {
      position: "relative",
      textAlign: "center",
      "&.image": {
        "& .image--wrapper": {
          aspectRatio: "16 /9",
          padding: "1rem",
          "& img": {
            mixBlendMode: "multiply",
          },
        },
        "& .category": {
          zIndex: "500",
          position: "absolute",
          top: "0",
          left: "0",
        },
      },
    },
    "& .category--tag": {
      fontWeight: `${basierCirleRegular.style.fontWeight} !important`,
      fontFamily: `${basierCirleRegular.style.fontFamily} !important`,
    },
    "& .MuiCardContent-root": {
      padding: "1rem 1rem 0 1rem",
      "& .location": {
        "&--title": {
          fontSize: "1.375rem",
          fontFamily: futuraNextCondensedExtraBold.style.fontFamily,
          color: theme.palette.common.black,
          marginBottom: ".5rem",
        },
        "&--subtitle": {
          fontWeight: basierCirleBold.style.fontWeight,
          fontFamily: basierCirleBold.style.fontFamily,
          textTransform: "uppercase",
          fontSize: ".875rem",
          lineHeight: "1rem",
          marginTop: "1rem",
        },
        "&--phone": {
          "& .MuiButton-root": {
            color: theme.palette.common.black,
            padding: "0",
            fontWeight: `${basierCirleRegular.style.fontWeight} !important`,
            fontFamily: `${basierCirleRegular.style.fontFamily} !important`,
            "&:hover": {
              color: theme.palette.primary.main,
              opacity: "1",
              "& svg": {
                fill: theme.palette.primary.main,
              },
            },
            "& svg": {
              width: "1rem",
              transition: ".2s ease-in-out",
            },
          },
        },
        "&--mail": {
          "& .MuiButton-root": {
            fontWeight: basierCirleRegular.style.fontWeight,
            fontFamily: basierCirleRegular.style.fontFamily,
            textTransform: "initial",
            color: theme.palette.secondary.main,
            padding: "0",
            "&:hover": {
              color: theme.palette.primary.main,
              opacity: "1",
              "& svg": {
                fill: theme.palette.primary.main,
              },
            },
            "& svg": {
              width: "1rem",
              fill: theme.palette.secondary.main,
              transition: ".2s ease-in-out",
            },
          },
        },
        "&--text": {
          whiteSpace: "pre-line",
          fontWeight: `${basierCirleRegular.style.fontWeight} !important`,
          fontFamily: `${basierCirleRegular.style.fontFamily} !important`,
        },
      },
      "& .MuiTypography-root": {
        textAlign: "center",
      },
    },
    "& .MuiGrid-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 28,
        [theme.breakpoints.up("md")]: {
          fontSize: 32,
        },
      },
    },
    "& .location-link": {
      fontWeight: `${basierCirleBold.style.fontWeight} !important`,
      fontFamily: `${basierCirleBold.style.fontFamily} !important`,
    },
  };

  const stylePreview = {
    backgroundColor: grey[200],
    aspectRatio: "1 / 1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "1rem",
    "& .MuiSvgIcon-root": {
      fontSize: "4rem",
      color: grey[400],
    },
    "& .MuiTypography-root": {
      textAlign: "center",
      textTransform: "uppercase",
      fontSize: "0.875rem",
      lineHeight: "1rem",
      color: grey[600],
    },
  };

  return (
    <>
      {location.category ? (
        <Card elevation={0} variant={variant} sx={style}>
          <CardMedia className={location.image && "image"}>
            <Box className={location.image && "category"}>
              <CategoryTag
                text={location.category.name}
                variant={`category--${location.category.name}`}
                iconCategory={true}
                className="category--tag"
              />
            </Box>
            {location.category.id + "" ===
              process.env.NEXT_PUBLIC_LOCATIONCATEGORYID_STUETZPUNKTE &&
            location.image ? (
              <Box className="image--wrapper">
                <StrapiImageAsset
                  strapiImageObject={location.image}
                  layout="responsive"
                  objectFit="contain"
                  width={160}
                  height={90}
                  fixedHeight={true}
                />
              </Box>
            ) : (
              ""
            )}
          </CardMedia>
          <CardContent>
            <Typography variant="h5" component="p" className="location--title">
              {location.name}
            </Typography>
            <Typography variant="body1" className="location--text">
              {location.address}
            </Typography>
            {location.contactperson && (
              <>
                <Typography variant="body1" className="location--subtitle">
                  Ansprechpartner:in
                </Typography>
                <Typography variant="body1" className="location--text">
                  {location.contactperson}
                </Typography>
              </>
            )}
            {location.capacity && (
              <>
                <Typography variant="body1" className="location--subtitle">
                  Kapazität
                </Typography>
                <Typography variant="body1" className="location--text">
                  {location.capacity} Personen
                </Typography>
              </>
            )}
            {location.phone && (
              <Grid
                container
                justifyContent="center"
                className="location--phone"
              >
                <Grid item xs="auto">
                  <Link href={`tel:${location.phone}`}>
                    <Button size="small" startIcon={<PhoneIcon />}>
                      {location.phone}
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            )}
            {location.email && (
              <Grid
                container
                justifyContent="center"
                className="location--mail"
              >
                <Grid item xs="auto">
                  <Link href={`mailto:${location.email}`}>
                    <Button size="small" startIcon={<MailIcon />}>
                      E-Mail schreiben
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            )}
            <Grid container justifyContent="center" sx={{ mb: 1 }}>
              {location.link_facebook && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_facebook}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton color="secondary">
                      <FacebookRoundedIcon />
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_instagram && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_instagram}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton color="secondary">
                      <InstagramIcon />
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_tiktok && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_tiktok}`}
                    passHref
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton>
                      <SvgIcon
                        color="secondary"
                        sx={{
                          fontSize: 32,
                          color: theme.palette.secondary.main,
                        }}
                      >
                        <TiktokIcon />
                      </SvgIcon>
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_twitter && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_twitter}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton color="secondary">
                      <TwitterIcon />
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_youtube && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_youtube}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton color="secondary">
                      <YouTubeIcon />
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_spotify && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_spotify}`}
                    passHref
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton>
                      <SvgIcon
                        color="secondary"
                        sx={{
                          fontSize: 32,
                          color: theme.palette.secondary.main,
                        }}
                      >
                        <SpotifyIcon />
                      </SvgIcon>
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.link_bandcamp && (
                <Grid item xs="auto">
                  <Link
                    href={`${location.link_bandcamp}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton>
                      <SvgIcon
                        color="secondary"
                        sx={{
                          fontSize: 32,
                          color: theme.palette.secondary.main,
                        }}
                      >
                        <BandcampIcon />
                      </SvgIcon>
                    </IconButton>
                  </Link>
                </Grid>
              )}
              {location.category?.name !== "Create Music Stützpunkt" &&
                location.description && (
                  <Grid item xs={12}>
                    <Typography variant="body1" className="location--subtitle">
                      Wir kümmern uns um
                    </Typography>
                    <ReadMore />
                  </Grid>
                )}
            </Grid>
          </CardContent>
          {location.website &&
            location.category?.name !== "Create Music Stützpunkt" && (
              <Box sx={{ marginTop: "auto" }}>
                <Button
                  component="a"
                  target="_blank"
                  rel="noopener"
                  href={`${location.website}`}
                  sx={{ marginTop: "1rem" }}
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  className="location-link"
                >
                  Zur Website
                </Button>
              </Box>
            )}
          {location.link_website && (
            <Box sx={{ marginTop: "auto" }}>
              <Button
                href={`${location.link_website}`}
                rel="noopener"
                target="_blank"
                component="a"
                sx={{ marginTop: "1rem" }}
                fullWidth
                variant="outlined"
                color="secondary"
                className="location-link"
              >
                Zur Website
              </Button>
            </Box>
          )}
          {location.category?.name === "Create Music Stützpunkt" &&
            page !== "Create" && (
              <Box sx={{ marginTop: "auto" }}>
                {page !== "CreatemusicVenuePage" && (
                  <Button
                    href={`/netzwerk/locations/${location.id}`}
                    component={Link}
                    sx={{ marginTop: "1rem" }}
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    className="location-link"
                  >
                    Mehr Infos anzeigen
                  </Button>
                )}
              </Box>
            )}
        </Card>
      ) : (
        <Box sx={stylePreview}>
          <Grid container justifyContent="center">
            <Grid item xs="auto">
              <HelpIcon />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                Noch keine Kategorie ausgewählt
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
